import React, { useState, useEffect } from 'react';
import {
  FaFileInvoice,
  FaSearch,
  FaFilter,
  FaPlus,
  FaEye,
  FaEnvelope,
  FaCheckCircle,
  FaTimes,
  FaClock,
  FaEdit,
  FaTrash,
  FaDownload,
  FaCalendar,
  FaDollarSign,
  FaUser,
  FaBuilding,
  FaPhone,
  FaChevronLeft,
  FaChevronRight,
  FaSpinner,
  FaUserPlus
} from 'react-icons/fa';
import { API_ENDPOINTS } from '../../config/api';
import { PUBLIC_SITE_URL } from '../../constants/branding';
import ITServicesQuoteForm from './ITServicesQuoteForm';
import ITServicesQuoteEmailComposer from './ITServicesQuoteEmailComposer';
import itCustomerService from '../services/itCustomerService';
import { useAdminAuth } from '../context/AdminAuthContext';

const ITServicesQuoteManagement = () => {
  const { token } = useAdminAuth();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [composingQuote, setComposingQuote] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    loadQuotes();
  }, [token]);

  const getHeaders = () => {
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': token } : {})
    };
  };

  const loadQuotes = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(API_ENDPOINTS.IT_SERVICES_QUOTE_MANAGEMENT, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          action: 'listQuotes'
        })
      });

      const data = await response.json();

      if (data.success) {
        setQuotes(data.quotes || []);
      } else {
        setError(data.error || 'Failed to load quotes');
      }
    } catch (err) {
      console.error('Error loading quotes:', err);
      setError('Failed to load quotes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Opens the compose step rather than sending immediately — recipients, subject
  // and message are reviewed there before anything reaches the customer.
  const handleSendQuote = (quoteId) => {
    const quote = quotes.find((q) => q.quoteId === quoteId) || { quoteId };
    setComposingQuote(quote);
  };

  const handleCreateInvoice = async (quoteId) => {
    if (!window.confirm('Create a tax invoice for this quote?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(API_ENDPOINTS.IT_SERVICES_INVOICE, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          action: 'createInvoice',
          quoteId: quoteId
        })
      });

      const data = await response.json();

      if (data.success) {
        alert(`Invoice ${data.invoice.invoiceNumber} created successfully!`);
        loadQuotes(); // Reload to update UI with invoice link
      } else {
        alert(data.error || 'Failed to create invoice');
      }
    } catch (err) {
      console.error('Error creating invoice:', err);
      alert('Failed to create invoice. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async (invoiceId) => {
    try {
      const response = await fetch(API_ENDPOINTS.IT_SERVICES_INVOICE, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          action: 'getDownloadUrl',
          invoiceId: invoiceId
        })
      });

      const data = await response.json();

      if (data.success && data.url) {
        window.open(data.url, '_blank');
      } else {
        alert(data.error || 'Failed to get download URL');
      }
    } catch (err) {
      console.error('Error downloading invoice:', err);
      alert('Failed to download invoice. Please try again.');
    }
  };

  const handleViewQuote = async (quoteId) => {
    try {
      const response = await fetch(API_ENDPOINTS.IT_SERVICES_QUOTE_MANAGEMENT, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          action: 'getQuote',
          quoteId: quoteId
        })
      });

      const data = await response.json();

      if (data.success && data.quote) {
        setSelectedQuote(data.quote);
        setShowViewModal(true);
      } else {
        alert(data.error || 'Failed to load quote');
      }
    } catch (err) {
      console.error('Error loading quote:', err);
      alert('Failed to load quote. Please try again.');
    }
  };

  const handleEditQuote = async (quoteId) => {
    try {
      const response = await fetch(API_ENDPOINTS.IT_SERVICES_QUOTE_MANAGEMENT, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          action: 'getQuote',
          quoteId: quoteId
        })
      });

      const data = await response.json();

      if (data.success && data.quote) {
        setEditingQuote(data.quote);
        setShowEditModal(true);
      } else {
        alert(data.error || 'Failed to load quote');
      }
    } catch (err) {
      console.error('Error loading quote:', err);
      alert('Failed to load quote. Please try again.');
    }
  };

  const handleSyncToCustomer = async (quoteId) => {
    try {
      setLoading(true);
      const data = await itCustomerService.syncQuote(quoteId);
      if (data.success) {
        alert(data.alreadySynced ? 'Customer is already synced.' : 'Customer record created/updated successfully!');
        loadQuotes();
      } else {
        alert(data.error || 'Failed to sync customer');
      }
    } catch (err) {
      console.error('Error syncing customer:', err);
      alert('Failed to sync customer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleQuoteSaved = (savedQuote) => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setEditingQuote(null);
    loadQuotes(); // Reload quotes list
    if (savedQuote) {
      alert(`Quote ${savedQuote.quoteNumber} saved successfully!`);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-300', label: 'Pending' },
      sent: { bg: 'bg-blue-500/20', text: 'text-blue-300', label: 'Sent' },
      accepted: { bg: 'bg-green-500/20', text: 'text-green-300', label: 'Accepted' },
      rejected: { bg: 'bg-red-500/20', text: 'text-red-300', label: 'Rejected' },
      expired: { bg: 'bg-gray-500/20', text: 'text-gray-300', label: 'Expired' },
      completed: { bg: 'bg-purple-500/20', text: 'text-purple-300', label: 'Completed' }
    };

    const badge = badges[status] || badges.pending;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const getPaymentStatusBadge = (paymentStatus) => {
    const badges = {
      unpaid: { bg: 'bg-yellow-500/20', text: 'text-yellow-300', label: 'Unpaid' },
      paid: { bg: 'bg-green-500/20', text: 'text-green-300', label: 'Paid' },
      partial: { bg: 'bg-blue-500/20', text: 'text-blue-300', label: 'Partial' },
      failed: { bg: 'bg-red-500/20', text: 'text-red-300', label: 'Failed' }
    };

    const badge = badges[paymentStatus] || badges.unpaid;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  // Filter quotes
  const filteredQuotes = quotes.filter(quote => {
    const matchesSearch =
      quote.quoteNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      quote.customerName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      quote.customerEmail?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      quote.customerCompany?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = statusFilter === 'all' || quote.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Pagination
  const totalPages = Math.ceil(filteredQuotes.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedQuotes = filteredQuotes.slice(startIndex, startIndex + itemsPerPage);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <FaSpinner className="animate-spin text-4xl text-[#FFD700]" />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-[#ebdbb2] mb-2">IT Services Quote Management</h1>
        <p className="text-[#a89984]">Manage quotes, send quotes to customers, and track payments</p>
      </div>

      {/* Actions Bar */}
      <div className="bg-[#3c3836] rounded-lg p-4 mb-6 flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="flex flex-1 gap-4 w-full md:w-auto">
          {/* Search */}
          <div className="relative flex-1 md:flex-initial">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#a89984]" />
            <input
              type="text"
              placeholder="Search quotes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 bg-[#282828] border border-[#504945] rounded-lg text-[#ebdbb2] placeholder-[#a89984] focus:outline-none focus:border-[#FFD700] w-full md:w-64"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 bg-[#282828] border border-[#504945] rounded-lg text-[#ebdbb2] focus:outline-none focus:border-[#FFD700]"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="sent">Sent</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
            <option value="expired">Expired</option>
            <option value="completed">Completed</option>
          </select>
        </div>

        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-gradient-to-r from-[#FFD700] to-[#FFB700] text-[#1d2021] px-6 py-2 rounded-lg font-semibold hover:shadow-lg transition-all flex items-center space-x-2"
        >
          <FaPlus />
          <span>Create Quote</span>
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 mb-6">
          <p className="text-red-300">{error}</p>
        </div>
      )}

      {/* Quotes Table */}
      <div className="bg-[#3c3836] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#282828]">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-[#ebdbb2] uppercase tracking-wider">Quote #</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-[#ebdbb2] uppercase tracking-wider">Customer</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-[#ebdbb2] uppercase tracking-wider">Service</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-[#ebdbb2] uppercase tracking-wider">Amount</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-[#ebdbb2] uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-[#ebdbb2] uppercase tracking-wider">Payment</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-[#ebdbb2] uppercase tracking-wider">Date</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-[#ebdbb2] uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#504945]">
              {paginatedQuotes.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-[#a89984]">
                    {searchQuery || statusFilter !== 'all' ? 'No quotes found matching your filters' : 'No quotes found'}
                  </td>
                </tr>
              ) : (
                paginatedQuotes.map((quote) => (
                  <tr key={quote.quoteId} className="hover:bg-[#504945] transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-[#ebdbb2] font-medium">{quote.quoteNumber}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-[#ebdbb2] font-medium">{quote.customerName}</div>
                      <div className="text-[#a89984] text-sm">{quote.customerEmail}</div>
                      {quote.customerCompany && (
                        <div className="text-[#a89984] text-sm">{quote.customerCompany}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-[#ebdbb2]">{quote.quoteTitle || quote.serviceType}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-[#ebdbb2] font-semibold">{formatCurrency(quote.totalAmount)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(quote.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getPaymentStatusBadge(quote.paymentStatus)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-[#ebdbb2]">{formatDate(quote.createdAt)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleViewQuote(quote.quoteId)}
                          className="p-2 bg-[#504945] text-[#ebdbb2] rounded hover:bg-[#665c54] transition-colors"
                          title="View Quote"
                        >
                          <FaEye />
                        </button>
                        {quote.status !== 'completed' && quote.status !== 'paid' && (
                          <button
                            onClick={() => handleEditQuote(quote.quoteId)}
                            className="p-2 bg-blue-500/20 text-blue-300 rounded hover:bg-blue-500/30 transition-colors"
                            title="Edit Quote"
                          >
                            <FaEdit />
                          </button>
                        )}
                        {(quote.status === 'pending' || quote.status === 'sent') && (
                          <button
                            onClick={() => handleSendQuote(quote.quoteId)}
                            className="p-2 bg-blue-500/20 text-blue-300 rounded hover:bg-blue-500/30 transition-colors"
                            title="Send Quote"
                          >
                            <FaEnvelope />
                          </button>
                        )}
                        {(quote.status === 'accepted' || quote.status === 'completed') && !quote.invoiceId && (
                          <button
                            onClick={() => handleCreateInvoice(quote.quoteId)}
                            className="p-2 bg-yellow-500/20 text-yellow-300 rounded hover:bg-yellow-500/30 transition-colors"
                            title="Create Invoice"
                          >
                            <FaFileInvoice />
                          </button>
                        )}
                        {quote.invoiceId && (
                          <div className="flex items-center space-x-1">
                            <a
                              href={`${PUBLIC_SITE_URL}/itservices/invoice/${encodeURIComponent(quote.invoiceId)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 bg-green-500/20 text-green-300 rounded hover:bg-green-500/30 transition-colors"
                              title="View Invoice Page"
                            >
                              <FaFileInvoice />
                            </a>
                            <button
                              onClick={() => handleDownloadInvoice(quote.invoiceId)}
                              className="p-2 bg-purple-500/20 text-purple-300 rounded hover:bg-purple-500/30 transition-colors"
                              title="Download PDF Invoice"
                            >
                              <FaDownload />
                            </button>
                          </div>
                        )}
                        {(quote.status === 'accepted' || quote.status === 'completed' || quote.paymentStatus === 'paid') && (
                          <button
                            onClick={() => handleSyncToCustomer(quote.quoteId)}
                            className="p-2 bg-orange-500/20 text-orange-400 rounded hover:bg-orange-500/30 transition-colors"
                            title="Sync to IT Customer Dashboard"
                          >
                            <FaUserPlus />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-[#282828] px-6 py-4 flex items-center justify-between border-t border-[#504945]">
            <div className="text-[#a89984] text-sm">
              Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredQuotes.length)} of {filteredQuotes.length} quotes
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="p-2 bg-[#504945] text-[#ebdbb2] rounded hover:bg-[#665c54] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <FaChevronLeft />
              </button>
              <span className="text-[#ebdbb2] px-4">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="p-2 bg-[#504945] text-[#ebdbb2] rounded hover:bg-[#665c54] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <FaChevronRight />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* View Quote Modal */}
      {showViewModal && selectedQuote && (
        <QuoteViewModal
          quote={selectedQuote}
          onClose={() => {
            setShowViewModal(false);
            setSelectedQuote(null);
          }}
          onRefresh={loadQuotes}
          onCreateInvoice={handleCreateInvoice}
          onDownloadInvoice={handleDownloadInvoice}
        />
      )}

      {/* Create Quote Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowCreateModal(false)}>
          <div className="bg-[#3c3836] rounded-lg p-6 max-w-5xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-[#ebdbb2]">Create New Quote</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-[#a89984] hover:text-[#ebdbb2]"
              >
                <FaTimes className="text-xl" />
              </button>
            </div>
            <ITServicesQuoteForm
              onSave={handleQuoteSaved}
              onCancel={() => setShowCreateModal(false)}
            />
          </div>
        </div>
      )}

      {/* Edit Quote Modal */}
      {showEditModal && editingQuote && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => {
          setShowEditModal(false);
          setEditingQuote(null);
        }}>
          <div className="bg-[#3c3836] rounded-lg p-6 max-w-5xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-[#ebdbb2]">Edit Quote: {editingQuote.quoteNumber}</h2>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingQuote(null);
                }}
                className="text-[#a89984] hover:text-[#ebdbb2]"
              >
                <FaTimes className="text-xl" />
              </button>
            </div>
            <ITServicesQuoteForm
              quote={editingQuote}
              onSave={handleQuoteSaved}
              onCancel={() => {
                setShowEditModal(false);
                setEditingQuote(null);
              }}
            />
          </div>
        </div>
      )}

      {composingQuote && (
        <ITServicesQuoteEmailComposer
          quote={composingQuote}
          onClose={() => setComposingQuote(null)}
          onSent={() => {
            setComposingQuote(null);
            alert('Quote sent successfully!');
            loadQuotes(); // Reload to pick up the new 'sent' status
          }}
        />
      )}
    </div>
  );
};

// Quote View Modal Component
const QuoteViewModal = ({ quote, onClose, onRefresh, onCreateInvoice, onDownloadInvoice }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-[#3c3836] rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-[#ebdbb2]">Quote Details</h2>
          <button
            onClick={onClose}
            className="text-[#a89984] hover:text-[#ebdbb2]"
          >
            <FaTimes className="text-xl" />
          </button>
        </div>

        {/* Quote Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-[#282828] rounded-lg p-4">
            <div className="text-[#a89984] text-sm mb-1">Quote Number</div>
            <div className="text-[#ebdbb2] font-semibold">{quote.quoteNumber}</div>
          </div>
          <div className="bg-[#282828] rounded-lg p-4">
            <div className="text-[#a89984] text-sm mb-1">Status</div>
            <div className="text-[#ebdbb2] font-semibold">{quote.status}</div>
          </div>
          <div className="bg-[#282828] rounded-lg p-4">
            <div className="text-[#a89984] text-sm mb-1">Created</div>
            <div className="text-[#ebdbb2]">{formatDate(quote.createdAt)}</div>
          </div>
          <div className="bg-[#282828] rounded-lg p-4">
            <div className="text-[#a89984] text-sm mb-1">Expires</div>
            <div className="text-[#ebdbb2]">{formatDate(quote.expiresAt)}</div>
          </div>
        </div>

        {/* Customer Info */}
        <div className="bg-[#282828] rounded-lg p-4 mb-6">
          <h3 className="text-xl font-bold text-[#ebdbb2] mb-4">Customer Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-[#a89984] text-sm mb-1">Name</div>
              <div className="text-[#ebdbb2]">{quote.customerName}</div>
            </div>
            <div>
              <div className="text-[#a89984] text-sm mb-1">Email</div>
              <div className="text-[#ebdbb2]">{quote.customerEmail}</div>
            </div>
            {quote.customerCompany && (
              <div>
                <div className="text-[#a89984] text-sm mb-1">Company</div>
                <div className="text-[#ebdbb2]">{quote.customerCompany}</div>
              </div>
            )}
            {quote.customerPhone && (
              <div>
                <div className="text-[#a89984] text-sm mb-1">Phone</div>
                <div className="text-[#ebdbb2]">{quote.customerPhone}</div>
              </div>
            )}
          </div>
        </div>

        {/* Line Items */}
        <div className="bg-[#282828] rounded-lg p-4 mb-6">
          <h3 className="text-xl font-bold text-[#ebdbb2] mb-4">Services</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#504945]">
                  <th className="text-left py-2 px-4 text-[#a89984]">Description</th>
                  <th className="text-right py-2 px-4 text-[#a89984]">Quantity</th>
                  <th className="text-right py-2 px-4 text-[#a89984]">Unit Price</th>
                  <th className="text-right py-2 px-4 text-[#a89984]">Total</th>
                </tr>
              </thead>
              <tbody>
                {quote.lineItems && quote.lineItems.map((item, index) => (
                  <tr key={index} className="border-b border-[#504945]">
                    <td className="py-3 px-4 text-[#ebdbb2]">{item.description}</td>
                    <td className="py-3 px-4 text-[#ebdbb2] text-right">{item.quantity || 1}</td>
                    <td className="py-3 px-4 text-[#ebdbb2] text-right">{formatCurrency(item.unitPrice || item.total)}</td>
                    <td className="py-3 px-4 text-[#ebdbb2] text-right font-semibold">{formatCurrency(item.total)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="3" className="py-3 px-4 text-[#ebdbb2] font-semibold text-right">Subtotal:</td>
                  <td className="py-3 px-4 text-[#ebdbb2] font-semibold text-right">{formatCurrency(quote.subtotal)}</td>
                </tr>
                {quote.tax > 0 && (
                  <tr>
                    <td colSpan="3" className="py-3 px-4 text-[#ebdbb2] text-right">Tax (GST):</td>
                    <td className="py-3 px-4 text-[#ebdbb2] text-right">{formatCurrency(quote.tax)}</td>
                  </tr>
                )}
                {/* Mirrors the customer quote page, which already shows this row —
                    without it an admin reviewing a discounted quote sees line items
                    that don't reconcile against the total. */}
                {quote.discount > 0 && (
                  <tr>
                    <td colSpan="3" className="py-3 px-4 text-green-400 text-right">Discount:</td>
                    <td className="py-3 px-4 text-green-400 text-right">-{formatCurrency(quote.discount)}</td>
                  </tr>
                )}
                <tr className="border-t-2 border-[#FFD700]">
                  <td colSpan="3" className="py-3 px-4 text-[#ebdbb2] font-bold text-xl text-right">Total:</td>
                  <td className="py-3 px-4 text-[#ebdbb2] font-bold text-xl text-right">{formatCurrency(quote.totalAmount)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-4">
          {quote.invoiceId ? (
            <>
              <a
                href={`${PUBLIC_SITE_URL}/itservices/invoice/${encodeURIComponent(quote.invoiceId)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-green-500/20 text-green-300 px-6 py-2 rounded-lg font-semibold hover:bg-green-500/30 transition-colors flex items-center space-x-2"
              >
                <FaFileInvoice />
                <span>View Invoice</span>
              </a>
              <button
                onClick={() => onDownloadInvoice(quote.invoiceId)}
                className="bg-purple-500/20 text-purple-300 px-6 py-2 rounded-lg font-semibold hover:bg-purple-500/30 transition-colors flex items-center space-x-2"
              >
                <FaDownload />
                <span>Download PDF</span>
              </button>
            </>
          ) : (
            (quote.status === 'accepted' || quote.status === 'completed') && (
              <button
                onClick={() => {
                  onClose();
                  onCreateInvoice(quote.quoteId);
                }}
                className="bg-yellow-500/20 text-yellow-300 px-6 py-2 rounded-lg font-semibold hover:bg-yellow-500/30 transition-colors flex items-center space-x-2"
              >
                <FaFileInvoice />
                <span>Create Invoice</span>
              </button>
            )
          )}
          <a
            href={`${PUBLIC_SITE_URL}/itservices/quote/${encodeURIComponent(quote.quoteId)}${
              quote.accessToken ? `?t=${encodeURIComponent(quote.accessToken)}` : ''
            }`}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-blue-500/20 text-blue-300 px-6 py-2 rounded-lg font-semibold hover:bg-blue-500/30 transition-colors flex items-center space-x-2"
          >
            <FaEye />
            <span>View Quote Page</span>
          </a>
          <button
            onClick={onClose}
            className="bg-[#504945] text-[#ebdbb2] px-6 py-2 rounded-lg font-semibold hover:bg-[#665c54] transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div >
  );
};

export default ITServicesQuoteManagement;

